/**
 * 掲示板 一覧画面 (v3.5 Board / Phase 2 → 2026-07-29 受信箱ビュー化)。
 * 仕様書: docs/02_work/done/plan_board_feature.md §1-2 / docs/mockups/board_ux_detail_A.html
 *
 * URL: /board
 *
 * 設計の骨格 = 2 つの直交する軸:
 *   関係性 (排他)     … セグメントタブ「あなた宛 / あなたの投稿 / すべて」
 *   状態 (重ねがけ可) … クイックフィルタ「未確認 / 未対応 / 期日超過 / 💬スレッド新着」
 * カテゴリーは「すべて」タブのグルーピング + 全タブ共通の絞り込みチップ。
 *
 * 旧 UI の問題と対応:
 * - 自分宛/自分の投稿/その他が行から読み取れない → 関係タグ + 関係性カラーバー + タブ
 * - 「自分の投稿に新しいアクション」が件数バッジ止まりで辿れない → タブ + 専用セクション
 * - 対応完了/クローズが同列で一覧が伸びる → クローズは末尾で折りたたみ、検索で拾う
 * - スレッド返信は対応ステータスを動かさないため完了行に埋もれる → 最上部へ浮上させる
 */
import type { BoardPostType, BoardUrgency } from '@goenhub/shared';
import {
  BOARD_POST_TYPE_LABEL,
  BOARD_URGENCY_LABEL,
  canViewBoardPost,
  isBoardPostPendingPublish,
  isBoardPostRestricted,
} from '@goenhub/shared';
import {
  Archive,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Folder,
  Lock,
  MessageSquare,
  Pencil,
  Pin,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BOARD_TYPE_VARIANT,
  BOARD_URGENCY_BAR_COLOR,
  BOARD_URGENCY_VARIANT,
  boardDueChip,
  boardOverallStatus,
  todayYmd,
} from './boardMeta';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useFeatureActions } from '@/features/permissions';
import { useAdminDirectory } from '@/hooks/useAdminDirectory';
import { useAuth } from '@/hooks/useAuth';
import { useBoardAttention } from '@/hooks/useBoardAttention';
import {
  useBoardCategories,
  useDeleteBoardCategory,
  useRenameBoardCategory,
  type BoardCategoryRecord,
} from '@/hooks/useBoardCategories';
import { useBoardPosts, type BoardPostRecord } from '@/hooks/useBoardPosts';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatDate, toDate } from '@/lib/firestoreTime';
import { paths } from '@/routes/paths';

/** 未分類セクションのキー（categoryId=null / 削除済みカテゴリーの投稿を束ねる）。 */
const UNCATEGORIZED_KEY = '__uncategorized__';

const NEW_CHIP_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * 対応状態フィルタ (§1-2)。既定 = 'default'（クローズ以外）。
 *
 * 2026-07-29: 範囲の広い順に並べ替え、「クローズのみ」を追加。
 *   all        … 制限なし（クローズ済みも含む全件）
 *   default    … クローズ以外（既定）
 *   open       … 対応中
 *   done       … 対応完了（クローズ前）
 *   closedOnly … クローズ済みだけ
 */
type StateFilter = 'all' | 'default' | 'open' | 'done' | 'closedOnly';

const STATE_FILTERS: Array<{ key: StateFilter; label: string }> = [
  { key: 'all', label: 'すべて' },
  { key: 'default', label: 'クローズ以外' },
  { key: 'open', label: '対応中' },
  { key: 'done', label: '対応完了' },
  { key: 'closedOnly', label: 'クローズのみ' },
];

/** クイックフィルタ ('' = なし)。関係性タブとは直交し、どのタブでも押せる。 */
type TriageFilter = '' | 'unread' | 'open' | 'overdue' | 'thread';

const URGENCY_RANK: Record<BoardUrgency, number> = { high: 0, medium: 1, low: 2 };

/** 関係性タブ (排他)。 */
type RelationTab = 'mine' | 'authored' | 'all';

/** 自分とその投稿の関係。投稿者かつ宛先の場合は 'by-me' を優先する。 */
type Relation = 'to-me' | 'by-me' | 'other';

function relationOf(post: BoardPostRecord, uid: string | null): Relation {
  if (!uid) return 'other';
  if (post.authorUid === uid) return 'by-me';
  if ((post.recipientUids ?? []).includes(uid)) return 'to-me';
  return 'other';
}

/** 関係性ごとの左バー色。緊急度はバッジで表現済みのためバーは関係性に割り当てる。 */
const RELATION_BAR: Record<Relation, string | null> = {
  'to-me': 'var(--accent)',
  'by-me': 'var(--accent-purple)',
  other: null,
};

const RELATION_TAG: Record<Relation, { label: string; className: string } | null> = {
  'to-me': {
    label: 'あなた宛',
    className: 'bg-[var(--accent-subtle)] text-[var(--accent)]',
  },
  'by-me': {
    label: 'あなたの投稿',
    className: 'bg-[var(--accent-purple-subtle)] text-[var(--accent-purple)]',
  },
  other: null,
};

/** 自分から見たスレッド新着数 (参加者でなければ 0)。 */
function threadUnreadOf(post: BoardPostRecord, uid: string | null): number {
  if (!uid) return 0;
  const participant = post.authorUid === uid || (post.recipientUids ?? []).includes(uid);
  if (!participant) return 0;
  return Math.max(0, (post.threadCount ?? 0) - (post.threadSeen?.[uid] ?? 0));
}

export function BoardListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const acts = useFeatureActions('board.posts');
  const posts = useBoardPosts();
  const categories = useBoardCategories();
  const directory = useAdminDirectory();
  const attention = useBoardAttention();
  const renameCategory = useRenameBoardCategory();
  const deleteCategory = useDeleteBoardCategory();

  const uid = user?.uid ?? null;

  const [typeFilter, setTypeFilter] = useState<BoardPostType | ''>('');
  const [urgencyFilter, setUrgencyFilter] = useState<BoardUrgency | ''>('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('default');
  const [tab, setTab] = useState<RelationTab>('mine');
  const [triage, setTriage] = useState<TriageFilter>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  // クローズ済みは既定で畳む。「あなた宛 / あなたの投稿」タブの末尾に出す。
  const [showClosed, setShowClosed] = useState(false);
  // 既定 = 全セクション展開。閉じたセクションのキーだけを保持する。
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // スマホ: フィルタ群はボトムシートに畳む（縦幅の大半をフィルタが占めるのを避ける）
  const isMobile = useIsMobile();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const searchQuery = search.trim().toLowerCase();
  // 検索中はクローズ済みも対象にする（アーカイブ検索の代わり）。
  const searching = searchQuery.length > 0;

  /** uid → 表示名（宛先表示用。解決不可の uid はそのまま表示）。 */
  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of directory.data ?? []) {
      map.set(m.uid, m.fullName || m.displayName || m.uid);
    }
    return (id: string) => map.get(id) ?? id;
  }, [directory.data]);

  /**
   * タブ・クイックフィルタ・カテゴリー・検索を適用した結果。
   * クローズ済みの除外はここでは行わない（タブ側でセクション分けするため）。
   */
  /**
   * 自分に見える投稿 (canViewBoardPost)。
   * - 公開予約中 (未公開) の投稿は投稿者本人にしか見せない (2026-07-30)
   * - 閲覧制限 ON の投稿は宛先と投稿者本人にしか見せない (2026-09-07)
   * 通知・バッジ・期日リマインダーはサーバ側 (derive / notifyTriggers) で既に
   * 止まっているため、ここは一覧の見え方だけを担う。
   * 件数バッジも同じ母集団から数えるため、フィルタ前にここで一度だけ落とす。
   */
  const visiblePosts = useMemo(
    () => (posts.data ?? []).filter((p) => canViewBoardPost(p, uid)),
    [posts.data, uid],
  );

  const filtered = useMemo(() => {
    const today = todayYmd();
    return visiblePosts.filter((p) => {
      // 関係性タブ (排他)
      const rel = relationOf(p, uid);
      if (tab === 'mine' && rel !== 'to-me') return false;
      if (tab === 'authored' && rel !== 'by-me') return false;

      if (typeFilter && p.type !== typeFilter) return false;
      if (urgencyFilter && p.urgency !== urgencyFilter) return false;
      if (categoryFilter && (p.categoryId ?? '') !== categoryFilter) return false;

      // 検索 (タイトル / 本文 / 投稿者)。検索中はクローズ済みも対象。
      if (searching) {
        const hay = `${p.title} ${p.bodyText} ${p.authorName}`.toLowerCase();
        if (!hay.includes(searchQuery)) return false;
      }

      // 状態フィルタは「すべて」タブでのみ意味を持つ (他タブはセクションで表現)
      if (tab === 'all' && !searching) {
        const overall = boardOverallStatus(p);
        // 'all' は制限なし（クローズ済みも表示）
        if (stateFilter === 'default' && p.closedAt != null) return false;
        if (stateFilter === 'open' && overall?.kind !== 'open') return false;
        if (stateFilter === 'done' && overall?.kind !== 'done') return false;
        if (stateFilter === 'closedOnly' && p.closedAt == null) return false;
      }

      // クイックフィルタ (タブ横断)
      if (triage === 'unread' && !(uid && p.recipientStates?.[uid]?.state === 'unread'))
        return false;
      if (triage === 'open' && !(uid && (p.openRecipientUids ?? []).includes(uid))) return false;
      if (triage === 'thread' && threadUnreadOf(p, uid) === 0) return false;
      if (
        triage === 'overdue' &&
        !(
          uid &&
          (p.openRecipientUids ?? []).includes(uid) &&
          p.dueDate != null &&
          p.dueDate < today
        )
      )
        return false;
      return true;
    });
  }, [
    visiblePosts,
    tab,
    typeFilter,
    urgencyFilter,
    categoryFilter,
    stateFilter,
    triage,
    uid,
    searching,
    searchQuery,
  ]);

  /** 並び: 緊急度(高→低) → 期日昇順(無期日は最後) → 投稿日降順 (§1-2)。 */
  const sortByPriority = (a: BoardPostRecord, b: BoardPostRecord) => {
    const ur = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
    if (ur !== 0) return ur;
    if (a.dueDate !== b.dueDate) {
      if (a.dueDate == null) return 1;
      if (b.dueDate == null) return -1;
      return a.dueDate < b.dueDate ? -1 : 1;
    }
    const ad = toDate(a.createdAt)?.getTime() ?? 0;
    const bd = toDate(b.createdAt)?.getTime() ?? 0;
    return bd - ad;
  };

  /**
   * 関係性タブ内のセクション分け。
   *
   * スレッド新着は「対応完了でも返信が来ている」を拾うため最上部へ浮上させ、
   * 重複表示を避けるため下位セクションからは除外する。
   */
  const personalSections = useMemo(() => {
    const today = todayYmd();
    const thread: BoardPostRecord[] = [];
    const overdue: BoardPostRecord[] = [];
    const todo: BoardPostRecord[] = [];
    const awaitingClose: BoardPostRecord[] = [];
    const waiting: BoardPostRecord[] = [];
    const done: BoardPostRecord[] = [];
    const closed: BoardPostRecord[] = [];

    for (const p of filtered) {
      // スレッド新着はクローズ済みより先に判定する。
      // クローズ後に返信が来たケースを折りたたみの中に埋めてしまうと、
      // 「💬 スレッド新着 1」と出ているのに一覧のどこにも無い状態になるため
      // (2026-07-29 実機で発覚)。行には「クローズ」バッジが出るので状態は分かる。
      if (threadUnreadOf(p, uid) > 0) {
        thread.push(p);
        continue;
      }
      if (p.closedAt != null) {
        closed.push(p);
        continue;
      }
      if (tab === 'mine') {
        const isOpenForMe = Boolean(uid && (p.openRecipientUids ?? []).includes(uid));
        if (isOpenForMe && p.dueDate != null && p.dueDate < today) overdue.push(p);
        else if (isOpenForMe) todo.push(p);
        else done.push(p);
      } else {
        // authored: 追跡ありの投稿だけが「クローズ待ち / 相手の対応待ち」に分かれる
        if (p.audience === 'selected' && !p.isOpen) awaitingClose.push(p);
        else if (p.audience === 'selected' && p.isOpen) waiting.push(p);
        else done.push(p);
      }
    }
    for (const list of [thread, overdue, todo, awaitingClose, waiting, done, closed]) {
      list.sort(sortByPriority);
    }
    return { thread, overdue, todo, awaitingClose, waiting, done, closed };
  }, [filtered, uid, tab]);

  /** タブのバッジ件数（クイックフィルタ・検索の影響を受けない全体値）。 */
  const tabCounts = useMemo(() => {
    const all = visiblePosts;
    let mine = 0;
    let authored = 0;
    for (const p of all) {
      if (p.closedAt != null) continue;
      const rel = relationOf(p, uid);
      if (rel === 'to-me' && uid && (p.openRecipientUids ?? []).includes(uid)) mine++;
      if (rel === 'by-me' && (p.authorUnseenActivity || threadUnreadOf(p, uid) > 0)) authored++;
    }
    return { mine, authored, all: all.filter((p) => p.closedAt == null).length };
  }, [visiblePosts, uid]);

  /** クイックフィルタ「💬 スレッド新着」の件数（タブ非依存）。 */
  const threadNewsCount = useMemo(
    () => visiblePosts.filter((p) => threadUnreadOf(p, uid) > 0).length,
    [visiblePosts, uid],
  );

  /** カテゴリー別グルーピング。未分類 (null or 削除済 id) は末尾。 */
  const sections = useMemo(() => {
    const cats = categories.data ?? [];
    const catIds = new Set(cats.map((c) => c.id));
    const byKey = new Map<string, BoardPostRecord[]>();
    for (const p of filtered) {
      const key = p.categoryId && catIds.has(p.categoryId) ? p.categoryId : UNCATEGORIZED_KEY;
      const list = byKey.get(key) ?? [];
      list.push(p);
      byKey.set(key, list);
    }
    const result: Array<{
      key: string;
      name: string;
      category: BoardCategoryRecord | null;
      posts: BoardPostRecord[];
    }> = cats.map((c) => ({
      key: c.id,
      name: c.name,
      category: c,
      posts: byKey.get(c.id) ?? [],
    }));
    result.push({
      key: UNCATEGORIZED_KEY,
      name: '未分類',
      category: null,
      posts: byKey.get(UNCATEGORIZED_KEY) ?? [],
    });
    return result;
  }, [filtered, categories.data]);

  const toggle = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canManageCategory = (c: BoardCategoryRecord) =>
    Boolean(user && (user.isOwner || c.createdBy === user.uid));

  const handleRenameCategory = async (c: BoardCategoryRecord) => {
    const name = window.prompt('カテゴリー名を変更:', c.name);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === c.name) return;
    try {
      await renameCategory.mutateAsync({ categoryId: c.id, name: trimmed });
      toast.success('カテゴリー名を変更しました');
    } catch (err) {
      toast.error(`変更失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDeleteCategory = async (c: BoardCategoryRecord) => {
    if (
      !window.confirm(
        `カテゴリー「${c.name}」を削除します。所属する投稿は「未分類」へ移動します。よろしいですか？`,
      )
    )
      return;
    try {
      await deleteCategory.mutateAsync(c.id);
      toast.success('カテゴリーを削除しました（所属投稿は未分類へ移動）');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const pillClass = (active: boolean) =>
    'px-3 py-1 rounded-sm text-xs border transition-colors ' +
    (active
      ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
      : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]');

  const toggleTriage = (t: Exclude<TriageFilter, ''>) => setTriage((prev) => (prev === t ? '' : t));

  /** スマホの「絞り込み」ボタンに出す適用中フィルタ数。 */
  const activeFilterCount =
    (triage ? 1 : 0) +
    (categoryFilter ? 1 : 0) +
    (typeFilter ? 1 : 0) +
    (urgencyFilter ? 1 : 0) +
    (tab === 'all' && stateFilter !== 'default' ? 1 : 0);

  const clearFilters = () => {
    setTriage('');
    setCategoryFilter('');
    setTypeFilter('');
    setUrgencyFilter('');
    setStateFilter('default');
  };

  const openPost = (p: BoardPostRecord) => navigate(paths.boardPostDetail.replace(':postId', p.id));

  // '💬' バッジからは詳細を開いてスレッドを自動オープン (?thread=1)。
  const openPostThread = (p: BoardPostRecord) =>
    navigate(`${paths.boardPostDetail.replace(':postId', p.id)}?thread=1`);

  const isLoading = posts.isLoading || categories.isLoading;
  const isEmpty = (posts.data ?? []).length === 0;

  return (
    <Screen
      title="掲示板"
      description="決定確認・作業依頼・共有事項をカテゴリー別に管理します"
      actions={
        acts.create ? (
          <Button type="button" variant="primary" onClick={() => navigate(paths.boardNew)}>
            <Plus className="size-4 mr-1" />
            新規投稿
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {/* 検索: クローズ済みも対象にする = 実質のアーカイブ検索 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-muted)]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="タイトル・本文・投稿者で検索（クローズ済みも対象）"
            className="w-full pl-9 pr-3 py-2 text-[13px] rounded-md border border-[var(--border)] bg-[var(--bg-input)] outline-none focus:border-[var(--accent)]"
          />
        </div>

        {/* 関係性セグメント (排他) */}
        <div className="flex gap-0.5 p-1 rounded-md bg-[var(--bg-elevated)]">
          {(
            [
              { key: 'mine', label: 'あなた宛', count: tabCounts.mine, alert: true },
              {
                key: 'authored',
                label: 'あなたの投稿',
                count: tabCounts.authored,
                alert: true,
              },
              { key: 'all', label: 'すべて', count: tabCounts.all, alert: false },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                'flex-1 text-[13px] font-semibold py-1.5 rounded transition-colors ' +
                (tab === t.key
                  ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]')
              }
            >
              {t.label}
              {t.count > 0 && (
                <span
                  className={
                    'ml-1.5 text-[10px] tabular-nums rounded-full px-1.5 py-0.5 font-bold ' +
                    (t.alert
                      ? 'bg-[var(--accent-red)] text-white'
                      : 'bg-[var(--bg-hover)] text-[var(--text-muted)]')
                  }
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {isMobile ? (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => setFilterSheetOpen(true)}
            >
              <SlidersHorizontal className="size-3.5 mr-1" />
              絞り込み
              {activeFilterCount > 0 && (
                <span className="ml-1.5 rounded-full bg-[var(--accent)] text-white text-[10px] font-bold px-1.5 py-0.5 tabular-nums">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            {activeFilterCount > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                クリア
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* クイックフィルタ (状態) — タブ横断で重ねがけできる */}
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2.5 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-[var(--text-secondary)]">状態:</span>
              <button
                type="button"
                onClick={() => toggleTriage('unread')}
                className={pillClass(triage === 'unread')}
              >
                未確認 <span className="tabular-nums font-semibold">{attention.unread}</span>
              </button>
              <button
                type="button"
                onClick={() => toggleTriage('open')}
                className={pillClass(triage === 'open')}
              >
                未対応 <span className="tabular-nums font-semibold">{attention.open}</span>
              </button>
              <button
                type="button"
                onClick={() => toggleTriage('overdue')}
                className={pillClass(triage === 'overdue')}
              >
                期日超過 <span className="tabular-nums font-semibold">{attention.overdue}</span>
              </button>
              <button
                type="button"
                onClick={() => toggleTriage('thread')}
                className={pillClass(triage === 'thread')}
                title="スレッドに新しい返信がある投稿"
              >
                💬 スレッド新着{' '}
                <span className="tabular-nums font-semibold">{threadNewsCount}</span>
              </button>
            </div>

            {/* カテゴリー絞り込み (タブを切り替えても維持される) */}
            {(categories.data ?? []).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[var(--text-muted)]">カテゴリー:</span>
                <button
                  type="button"
                  onClick={() => setCategoryFilter('')}
                  className={pillClass(categoryFilter === '')}
                >
                  すべて
                </button>
                {(categories.data ?? []).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategoryFilter(categoryFilter === c.id ? '' : c.id)}
                    className={pillClass(categoryFilter === c.id)}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            {/* タイプ / 緊急度 / 状態フィルタ */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-muted)]">タイプ:</span>
                {(['', 'decision', 'task', 'info'] as const).map((t) => (
                  <button
                    key={t || 'all'}
                    type="button"
                    onClick={() => setTypeFilter(t as BoardPostType | '')}
                    className={pillClass(typeFilter === t)}
                  >
                    {t ? BOARD_POST_TYPE_LABEL[t as BoardPostType] : 'すべて'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-muted)]">緊急度:</span>
                {(['', 'high', 'medium', 'low'] as const).map((u) => (
                  <button
                    key={u || 'all'}
                    type="button"
                    onClick={() => setUrgencyFilter(u as BoardUrgency | '')}
                    className={pillClass(urgencyFilter === u)}
                  >
                    {u ? BOARD_URGENCY_LABEL[u as BoardUrgency] : 'すべて'}
                  </button>
                ))}
              </div>
              {tab === 'all' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--text-muted)]">状態:</span>
                  {STATE_FILTERS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setStateFilter(f.key)}
                      className={pillClass(stateFilter === f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Spinner />
          </div>
        ) : isEmpty ? (
          <EmptyState
            icon={Pin}
            title="投稿がまだありません"
            description={
              acts.create
                ? '右上の「新規投稿」から最初の投稿を作成できます'
                : '投稿が作成されるとここに表示されます'
            }
          />
        ) : (
          <div className="space-y-2">
            {/* ── 関係性タブ (あなた宛 / あなたの投稿): 状態でセクション分け ── */}
            {tab !== 'all' && (
              <>
                <PersonalSection
                  title="スレッドに新しい返信があります"
                  hint="対応完了・クローズ済みでも返信が来ているものを含みます"
                  icon={MessageSquare}
                  tone="teal"
                  posts={personalSections.thread}
                  uid={uid}
                  nameOf={nameOf}
                  onOpen={openPost}
                  onOpenThread={openPostThread}
                />
                {tab === 'mine' ? (
                  <>
                    <PersonalSection
                      title="期日超過・要対応"
                      icon={Pin}
                      tone="red"
                      posts={personalSections.overdue}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                    />
                    <PersonalSection
                      title="あなたの対応待ち"
                      icon={Pin}
                      tone="accent"
                      posts={personalSections.todo}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                    />
                    <PersonalSection
                      title="対応済み"
                      hint="あなたの作業は完了・投稿者のクローズ待ち"
                      icon={Pin}
                      tone="muted"
                      posts={personalSections.done}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                    />
                  </>
                ) : (
                  <>
                    <PersonalSection
                      title="相手が対応を終えました — 確認してクローズしてください"
                      icon={Pin}
                      tone="purple"
                      posts={personalSections.awaitingClose}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                    />
                    <PersonalSection
                      title="相手の対応待ち"
                      icon={Pin}
                      tone="accent"
                      posts={personalSections.waiting}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                    />
                    <PersonalSection
                      title="全員宛（対応追跡なし）"
                      icon={Pin}
                      tone="muted"
                      posts={personalSections.done}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                    />
                  </>
                )}

                {/* クローズ済み: 既定で畳む。検索中は自動展開する。 */}
                {personalSections.closed.length > 0 &&
                  (showClosed || searching || triage !== '' ? (
                    <PersonalSection
                      title="クローズ済み"
                      icon={Archive}
                      tone="muted"
                      posts={personalSections.closed}
                      uid={uid}
                      nameOf={nameOf}
                      onOpen={openPost}
                      onOpenThread={openPostThread}
                      onCollapse={
                        searching || triage !== '' ? undefined : () => setShowClosed(false)
                      }
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowClosed(true)}
                      className="w-full rounded-md border border-dashed border-[var(--border)] py-2.5 text-xs text-[var(--text-muted)] hover:bg-[var(--bg-hover)] transition-colors inline-flex items-center justify-center gap-1.5"
                    >
                      <Archive className="size-3.5" />
                      クローズ済み {personalSections.closed.length} 件を表示
                    </button>
                  ))}

                {personalSections.thread.length +
                  personalSections.overdue.length +
                  personalSections.todo.length +
                  personalSections.awaitingClose.length +
                  personalSections.waiting.length +
                  personalSections.done.length +
                  personalSections.closed.length ===
                  0 && (
                  <EmptyState
                    icon={Pin}
                    title={
                      searching
                        ? '該当する投稿がありません'
                        : tab === 'mine'
                          ? 'あなた宛の投稿はありません'
                          : 'あなたの投稿で動きのあるものはありません'
                    }
                    description={
                      searching
                        ? '検索語を変えてお試しください'
                        : '「すべて」タブから全体を確認できます'
                    }
                  />
                )}
              </>
            )}

            {tab === 'all' &&
              sections.map((section) => {
                // 未分類は投稿ゼロなら非表示 (カテゴリーは管理 UI アクセスのため常時表示)
                if (section.key === UNCATEGORIZED_KEY && section.posts.length === 0) return null;
                const isOpen = !collapsed.has(section.key);
                return (
                  <div
                    key={section.key}
                    className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden"
                  >
                    <div className="w-full flex items-center gap-3 p-3 hover:bg-[var(--bg-hover)] transition-colors">
                      <button
                        type="button"
                        onClick={() => toggle(section.key)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        {isOpen ? (
                          <ChevronDown className="size-4 text-[var(--text-muted)]" />
                        ) : (
                          <ChevronRight className="size-4 text-[var(--text-muted)]" />
                        )}
                        <Folder className="size-4 text-[var(--text-muted)]" />
                        <span className="text-sm font-medium truncate">{section.name}</span>
                        <span className="text-[11px] text-[var(--text-muted)] tabular-nums">
                          {section.posts.length} 件
                        </span>
                      </button>
                      {section.category && canManageCategory(section.category) && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => void handleRenameCategory(section.category!)}
                            title="カテゴリー名を変更"
                          >
                            <Pencil />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => void handleDeleteCategory(section.category!)}
                            title="カテゴリーを削除（所属投稿は未分類へ移動）"
                            disabled={deleteCategory.isPending}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      )}
                    </div>
                    {isOpen && section.posts.length > 0 && (
                      <div className="border-t border-[var(--border)]">
                        {section.posts.map((p) => (
                          <PostRow
                            key={p.id}
                            post={p}
                            uid={uid}
                            nameOf={nameOf}
                            onClick={() => openPost(p)}
                            onOpenThread={() => openPostThread(p)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* スマホ: フィルタ群のボトムシート */}
      {isMobile && (
        <BottomSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          title="絞り込み"
          footer={
            <div className="flex gap-2">
              <Button type="button" variant="secondary" className="flex-1" onClick={clearFilters}>
                条件をクリア
              </Button>
              <Button
                type="button"
                variant="primary"
                className="flex-1"
                onClick={() => setFilterSheetOpen(false)}
              >
                閉じる
              </Button>
            </div>
          }
        >
          <div className="space-y-4 pt-1">
            <FilterGroup label="状態">
              {(
                [
                  ['unread', `未確認 ${attention.unread}`],
                  ['open', `未対応 ${attention.open}`],
                  ['overdue', `期日超過 ${attention.overdue}`],
                  ['thread', `💬 スレッド新着 ${threadNewsCount}`],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleTriage(key)}
                  className={pillClass(triage === key)}
                >
                  {label}
                </button>
              ))}
            </FilterGroup>

            {(categories.data ?? []).length > 0 && (
              <FilterGroup label="カテゴリー">
                <button
                  type="button"
                  onClick={() => setCategoryFilter('')}
                  className={pillClass(categoryFilter === '')}
                >
                  すべて
                </button>
                {(categories.data ?? []).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategoryFilter(categoryFilter === c.id ? '' : c.id)}
                    className={pillClass(categoryFilter === c.id)}
                  >
                    {c.name}
                  </button>
                ))}
              </FilterGroup>
            )}

            <FilterGroup label="タイプ">
              {(['', 'decision', 'task', 'info'] as const).map((t) => (
                <button
                  key={t || 'all'}
                  type="button"
                  onClick={() => setTypeFilter(t as BoardPostType | '')}
                  className={pillClass(typeFilter === t)}
                >
                  {t ? BOARD_POST_TYPE_LABEL[t as BoardPostType] : 'すべて'}
                </button>
              ))}
            </FilterGroup>

            <FilterGroup label="緊急度">
              {(['', 'high', 'medium', 'low'] as const).map((u) => (
                <button
                  key={u || 'all'}
                  type="button"
                  onClick={() => setUrgencyFilter(u as BoardUrgency | '')}
                  className={pillClass(urgencyFilter === u)}
                >
                  {u ? BOARD_URGENCY_LABEL[u as BoardUrgency] : 'すべて'}
                </button>
              ))}
            </FilterGroup>

            {tab === 'all' && (
              <FilterGroup label="対応状態">
                {STATE_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setStateFilter(f.key)}
                    className={pillClass(stateFilter === f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </FilterGroup>
            )}
          </div>
        </BottomSheet>
      )}
    </Screen>
  );
}

/** ボトムシート内のフィルタ 1 グループ。 */
function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-[var(--text-muted)] mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

/** セクションの色調。関係性・緊急度の意味づけに合わせる。 */
const SECTION_TONE: Record<
  'red' | 'accent' | 'purple' | 'teal' | 'muted',
  { border: string; head: string; text: string }
> = {
  red: {
    border: 'border-[var(--accent-red)]',
    head: 'bg-[var(--accent-red-subtle)]',
    text: 'text-[var(--accent-red)]',
  },
  accent: {
    border: 'border-[var(--accent)]',
    head: 'bg-[var(--accent-subtle)]',
    text: 'text-[var(--accent)]',
  },
  purple: {
    border: 'border-[var(--accent-purple)]',
    head: 'bg-[var(--accent-purple-subtle)]',
    text: 'text-[var(--accent-purple)]',
  },
  teal: {
    border: 'border-[var(--accent-cyan)]',
    head: 'bg-[var(--accent-cyan-subtle)]',
    text: 'text-[var(--accent-cyan)]',
  },
  muted: {
    border: 'border-[var(--border)]',
    head: 'bg-[var(--bg-elevated)]',
    text: 'text-[var(--text-secondary)]',
  },
};

/**
 * 関係性タブ内の 1 セクション。件数 0 なら描画しない。
 * onCollapse を渡すと見出しから畳める（クローズ済みセクション用）。
 */
function PersonalSection({
  title,
  hint,
  icon: Icon,
  tone,
  posts,
  uid,
  nameOf,
  onOpen,
  onOpenThread,
  onCollapse,
}: {
  title: string;
  hint?: string;
  icon: typeof Pin;
  tone: keyof typeof SECTION_TONE;
  posts: BoardPostRecord[];
  uid: string | null;
  nameOf: (id: string) => string;
  onOpen: (p: BoardPostRecord) => void;
  onOpenThread: (p: BoardPostRecord) => void;
  onCollapse?: () => void;
}) {
  if (posts.length === 0) return null;
  const t = SECTION_TONE[tone];
  return (
    <div className={`rounded-md border ${t.border} bg-[var(--bg-surface)] overflow-hidden`}>
      <div
        className={`flex items-center gap-2 px-3 py-2.5 border-b border-[var(--border)] ${t.head}`}
      >
        <Icon className={`size-4 ${t.text}`} />
        <span className={`text-[13px] font-semibold ${t.text}`}>{title}</span>
        <span className="text-[11px] text-[var(--text-muted)] tabular-nums">{posts.length} 件</span>
        {hint && <span className="text-[11px] text-[var(--text-muted)] truncate">・{hint}</span>}
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            className="ml-auto text-[11px] text-[var(--text-muted)] hover:text-[var(--accent)]"
          >
            畳む
          </button>
        )}
      </div>
      <div>
        {posts.map((p) => (
          <PostRow
            key={p.id}
            post={p}
            uid={uid}
            nameOf={nameOf}
            onClick={() => onOpen(p)}
            onOpenThread={() => onOpenThread(p)}
          />
        ))}
      </div>
    </div>
  );
}

function PostRow({
  post,
  uid,
  nameOf,
  onClick,
  onOpenThread,
}: {
  post: BoardPostRecord;
  uid: string | null;
  nameOf: (id: string) => string;
  onClick: () => void;
  onOpenThread: () => void;
}) {
  const created = toDate(post.createdAt);
  // NEW チップは全員宛のみ (§1-2: 宛先指定は unread 視覚表現で代替)
  const isNew =
    post.audience !== 'selected' && created
      ? Date.now() - created.getTime() <= NEW_CHIP_WINDOW_MS
      : false;
  // 要約フォールバック (§7-2): aiSummary 未生成時は bodyText 先頭80字
  const summary = post.aiSummary ?? post.bodyText.slice(0, 80);
  const dueChip = post.dueDate ? boardDueChip(post.dueDate) : null;
  const overall = boardOverallStatus(post);
  const isClosed = post.closedAt != null;

  // 公開予約中 (未公開)。一覧に出ている時点で自分が投稿者。
  const pendingPublish = isBoardPostPendingPublish(post);
  const publishAtDate = pendingPublish ? toDate(post.publishAt) : null;
  const publishAtLabel = publishAtDate
    ? publishAtDate.toLocaleString('ja-JP', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

  // 閲覧制限 ON (宛先と投稿者本人にしか出ない)
  const restricted = isBoardPostRestricted(post);

  const unreadForMe = Boolean(uid && post.recipientStates?.[uid]?.state === 'unread');

  // スレッド新着 (§1-5): 参加者 (投稿者 or 宛先) で threadCount - threadSeen[me] > 0。
  const isParticipant = Boolean(
    uid && (post.authorUid === uid || (post.recipientUids ?? []).includes(uid)),
  );
  const threadUnread = isParticipant
    ? Math.max(0, (post.threadCount ?? 0) - (uid ? (post.threadSeen?.[uid] ?? 0) : 0))
    : 0;
  // 左3pxバー (2026-07-29): 緊急度はバッジで表現済みのため、バーは「自分との関係」を担う。
  // 関係なし (other) の投稿だけ従来どおり緊急度カラーで目立たせる。
  const relation = relationOf(post, uid);
  const barColor = RELATION_BAR[relation] ?? BOARD_URGENCY_BAR_COLOR[post.urgency] ?? null;
  const relTag = RELATION_TAG[relation];

  // 宛先表示 (§1-2): 全員 / あなた +N名 / 田中 +N名
  let audienceLabel: ReactNode = '全員';
  if (post.audience === 'selected') {
    const uids = post.recipientUids ?? [];
    const includesMe = Boolean(uid && uids.includes(uid));
    if (includesMe) {
      const others = uids.length - 1;
      audienceLabel = (
        <>
          <span className="text-[var(--accent)] font-medium">あなた</span>
          {others > 0 && ` +${others}名`}
        </>
      );
    } else if (uids.length > 0) {
      const others = uids.length - 1;
      audienceLabel = `${nameOf(uids[0])}${others > 0 ? ` +${others}名` : ''}`;
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={
        // スマホ: タップ領域を確保するため縦余白を増やし、押下フィードバックを付ける
        'relative w-full text-left px-4 py-3.5 pl-5 border-t border-[var(--border)] first:border-t-0 hover:bg-[var(--bg-hover)] active:bg-[var(--bg-hover)] transition-colors' +
        (isClosed ? ' opacity-60' : '')
      }
    >
      {/* 緊急度カラーバー (低はなし。未読時はアクセント色で代替) */}
      {barColor && (
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ backgroundColor: barColor }}
        />
      )}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* 公開予約中 (投稿者本人にしか出ない)。関係タグより先に置いて
                「これはまだ誰にも見えていない」ことを最初に読ませる。 */}
            {pendingPublish && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5 bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]">
                <CalendarClock className="size-3" />
                {publishAtLabel ? `${publishAtLabel} に公開` : '公開予約中'}
              </span>
            )}
            {restricted && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5 bg-[var(--bg-hover)] text-[var(--text-secondary)] border border-[var(--border)]"
                title="宛先と投稿者本人だけが閲覧できます"
              >
                <Lock className="size-3" />
                閲覧制限
              </span>
            )}
            {relTag && (
              <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${relTag.className}`}>
                {relTag.label}
              </span>
            )}
            <Badge variant={BOARD_TYPE_VARIANT[post.type]}>
              {BOARD_POST_TYPE_LABEL[post.type]}
            </Badge>
            <Badge variant={BOARD_URGENCY_VARIANT[post.urgency]}>
              緊急度: {BOARD_URGENCY_LABEL[post.urgency]}
            </Badge>
            <span
              className={
                'text-[13px] truncate ' +
                (isClosed
                  ? 'font-medium text-[var(--text-muted)]'
                  : unreadForMe
                    ? 'font-bold text-[var(--text-primary)]'
                    : 'font-medium')
              }
            >
              {post.title}
            </span>
            {unreadForMe && (
              <span
                className="size-2 rounded-full bg-[var(--accent)] flex-shrink-0"
                title="未確認"
              />
            )}
            {isNew && <Badge variant="sent">NEW</Badge>}
          </div>
          {summary && (
            <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">{summary}</p>
          )}
          <div className="text-[11px] text-[var(--text-muted)] mt-1.5 flex items-center gap-3 flex-wrap">
            <span>宛先: {audienceLabel}</span>
            {relation !== 'by-me' && <span>投稿者: {post.authorName}</span>}
            <span>{formatDate(post.createdAt)}</span>
            {relation === 'by-me' && post.authorUnseenActivity && (
              <span className="text-[var(--accent-purple)] font-semibold">
                新しいアクションがあります
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap md:flex-shrink-0 md:flex-nowrap">
          {/* スレッド新着バッジ (§1-5): クリックで詳細を開きスレッドを自動オープン。
              行全体は <button> のため入れ子回避で span + stopPropagation を使う。 */}
          {threadUnread > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onOpenThread();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenThread();
                }
              }}
              className="inline-flex items-center gap-1 rounded-full border border-[var(--accent)] bg-[var(--accent-subtle)] px-2 py-0.5 text-[11px] font-semibold text-[var(--accent)] tabular-nums hover:opacity-80"
              title="スレッドに新着があります"
            >
              💬 {threadUnread}
            </span>
          )}
          {overall && <Badge variant={overall.variant}>{overall.label}</Badge>}
          {dueChip && <Badge variant={dueChip.variant}>{dueChip.label}</Badge>}
        </div>
      </div>
    </button>
  );
}
